import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminSubscriptionsTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({
    name: '',
    interval: 'monthly',
    price: 299,
    description: '',
    features: '',
    is_active: true
  });

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/subscription/plans`);
      if (res.ok) setPlans(await res.json());
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setForm({ name: '', interval: 'monthly', price: 299, description: '', features: '', is_active: true });
    setShowDialog(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      interval: plan.interval,
      price: plan.price,
      description: plan.description,
      features: (plan.features || []).join('\n'),
      is_active: plan.is_active
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const payload = {
      ...form,
      features: form.features.split('\n').filter(f => f.trim()),
      price: parseFloat(form.price)
    };

    try {
      const url = editingPlan
        ? `${API}/admin/subscription/plans/${editingPlan.id}`
        : `${API}/admin/subscription/plans`;
      const method = editingPlan ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(`Plan ${editingPlan ? 'updated' : 'created'} successfully`);
        setShowDialog(false);
        fetchPlans();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save plan');
      }
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/admin/subscription/plans/${planId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Plan deleted');
        fetchPlans();
      }
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  return (
    <>
      <div className='mb-6 flex justify-between items-center'>
        <h2 className='text-2xl font-bold text-[#1A3C34]' style={{ fontFamily: 'Clash Display' }}>Subscription Plans</h2>
        <Button data-testid='create-plan-btn' onClick={openCreate} className='bg-[#D4F246] text-[#1A3C34] hover:bg-[#E2F585] rounded-full'>
          <Plus className='w-4 h-4 mr-2' /> Add Plan
        </Button>
      </div>

      {loading ? (
        <div className='text-center py-8'>Loading plans...</div>
      ) : plans.length === 0 ? (
        <Card className='p-12 bg-white rounded-2xl text-center'>
          <Crown className='w-16 h-16 text-gray-400 mx-auto mb-4' />
          <h3 className='text-xl font-bold text-[#1A3C34] mb-2'>No subscription plans</h3>
          <p className='text-gray-600 mb-6'>Create your first subscription plan to start earning recurring revenue</p>
          <Button onClick={openCreate} className='bg-[#D4F246] text-[#1A3C34] hover:bg-[#E2F585] rounded-full px-8'>
            Create Plan
          </Button>
        </Card>
      ) : (
        <div className='grid md:grid-cols-2 gap-6'>
          {plans.map((plan, index) => (
            <Card key={plan.id} className='p-6 bg-white rounded-2xl' data-testid={`plan-card-${index}`}>
              <div className='flex justify-between items-start mb-4'>
                <div>
                  <div className='flex items-center gap-2 mb-1'>
                    <Crown className='w-5 h-5 text-[#D4F246]' />
                    <h3 className='text-xl font-bold text-[#1A3C34]'>{plan.name}</h3>
                  </div>
                  <span className='text-xs bg-gray-100 px-2 py-1 rounded capitalize'>{plan.interval}</span>
                </div>
                <div className='flex gap-2'>
                  <Button size='sm' variant='outline' onClick={() => openEdit(plan)} data-testid={`edit-plan-${index}`}>
                    <Edit2 className='w-4 h-4' />
                  </Button>
                  <Button size='sm' variant='outline' onClick={() => handleDelete(plan.id)} className='text-red-600 hover:bg-red-50' data-testid={`delete-plan-${index}`}>
                    <Trash2 className='w-4 h-4' />
                  </Button>
                </div>
              </div>
              <div className='text-3xl font-bold text-[#1A3C34] mb-2'>
                R{plan.price.toFixed(0)}<span className='text-base font-normal text-gray-500'>/{plan.interval === 'annual' ? 'year' : 'month'}</span>
              </div>
              <p className='text-gray-600 text-sm mb-4'>{plan.description}</p>
              {plan.features?.length > 0 && (
                <ul className='space-y-1'>
                  {plan.features.map((f, i) => (
                    <li key={i} className='text-sm text-gray-600 flex items-center gap-2'>
                      <span className='text-[#D4F246]'>&#10003;</span> {f}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className='max-w-lg' data-testid='plan-dialog'>
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit' : 'Create'} Subscription Plan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className='space-y-4'>
            <div>
              <Label>Plan Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder='e.g., Monthly All-Access' required data-testid='plan-name-input' />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Interval</Label>
                <Select value={form.interval} onValueChange={(val) => setForm({...form, interval: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value='monthly'>Monthly</SelectItem>
                    <SelectItem value='annual'>Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (ZAR)</Label>
                <Input type='number' step='0.01' value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} required data-testid='plan-price-input' />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder='Brief plan description' rows={2} required />
            </div>
            <div>
              <Label>Features (one per line)</Label>
              <Textarea value={form.features} onChange={(e) => setForm({...form, features: e.target.value})} placeholder={'Access to all courses\nNew content monthly\nPriority support'} rows={4} />
            </div>
            <Button type='submit' className='w-full bg-[#1A3C34] text-white rounded-full' data-testid='save-plan-btn'>
              {editingPlan ? 'Update' : 'Create'} Plan
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
